import React, { useEffect, useState } from "react"
import { FaUsers, FaUserTie, FaFileAlt, FaFolderOpen, FaCloudUploadAlt, FaChartLine, FaCalendarAlt, FaArrowRight } from "react-icons/fa"
import { Card, CardBody, Col, Container, Row, Table, Badge, Input } from "reactstrap"
import { useNavigate } from "react-router-dom"

import 'chart.js/auto'; 
import { Line, Bar } from "react-chartjs-2"

import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import WelcomeComp from "./WelcomeComp"
import { fetchData } from "utils/Common_api_functions"
import Configs from "../../config"

const noop = () => { }

const Dashboard = () => {
  document.title = "Dashboard | Siddhi Tax Consultant"

  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  
  const [staffData, setStaffData] = useState([])
  const [customersData, setCustomersData] = useState([])
  const [customerTotal, setCustomerTotal] = useState(0)
  const [docTypesData, setDocTypesData] = useState([])
  const [categoryTypesData, setCategoryTypesData] = useState([])
  const [analyticsData, setAnalyticsData] = useState([])

  const [startDate, setStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0])

  useEffect(() => {
    fetchAll()
  }, [])

  useEffect(() => {
    fetchGraphData()
  }, [startDate, endDate])

  async function fetchAll() {
    const [staffRes, custRes, docRes, categoryRes] = await Promise.all([
      fetchData(setLoading, "staff"),
      fetchData(noop, "customer"),
      fetchData(noop, "document-types"),
      fetchData(noop, "document-category"),
    ])
    if (staffRes?.success) setStaffData(staffRes.data)
    if (custRes?.success) {
      setCustomersData(custRes.data)
      setCustomerTotal(custRes.paging?.total ?? custRes.data?.length ?? 0)
    }
    if (docRes?.success) setDocTypesData(docRes.data)
    if (categoryRes?.success) setCategoryTypesData(categoryRes.data)
  }

  async function fetchGraphData() {
    const res = await fetchData(noop, `analytics?start=${startDate}&end=${endDate}`)
    if (res?.success) {
      const sorted = [...res.data].sort((a, b) => new Date(a.date) - new Date(b.date));
      setAnalyticsData(sorted)
    }
  }

  const getChartData = (type) => {
    const filtered = analyticsData.filter(item => item.type === type);
    return {
      labels: filtered.map(item => item.date),
      values: filtered.map(item => Number(item.value))
    };
  };

  const customerStats = getChartData("customers");
  const fileStats = getChartData("files");

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { 
      y: { beginAtZero: true, ticks: { precision: 0 } },
      x: { grid: { display: false } } 
    }
  };

  return (
    <React.Fragment>
      {loading && <Loader />}
      <div className="page-content bg-light">
        <Container fluid>
          <Breadcrumb title="Siddhi Tax" breadcrumbItem="Admin Dashboard" />

          {/* ROW 1: STAT CARDS */}
          <Row className="mb-4">
            {[
              { label: "Total Staff", value: staffData.length, icon: <FaUsers />, color: "#556ee6", bg: "rgba(85, 110, 230, 0.1)" },
              { label: "Total Customers", value: customerTotal, icon: <FaUserTie />, color: "#34c38f", bg: "rgba(52, 195, 143, 0.1)" },
              { label: "Total Categories", value: categoryTypesData.length, icon: <FaFolderOpen />, color: "#f1b44c", bg: "rgba(241, 180, 76, 0.1)" },
              { label: "Doc Types", value: docTypesData.length, icon: <FaFileAlt />, color: "#74788d", bg: "rgba(116, 120, 141, 0.1)" },
            ].map((card, i) => (
              <Col key={i} md={6} xl={3}>
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4 d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-muted fw-medium mb-1">{card.label}</p>
                      <h3 className="mb-0 fw-bold">{card.value}</h3>
                    </div>
                    <div className="avatar-md rounded-circle d-flex align-items-center justify-content-center" 
                         style={{ backgroundColor: card.bg, color: card.color, width: '48px', height: '48px' }}>
                      {card.icon}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* ROW 2: FULL WIDTH GRAPHS */}
          <Row className="mb-4">
            <Col lg={12}>
              <Card className="border-0 shadow-sm">
                <CardBody>
                  <div className="d-md-flex justify-content-between align-items-center mb-4">
                    <h5 className="fw-bold mb-0">Performance Analytics</h5>
                    <div className="d-flex align-items-center bg-light rounded px-2 border">
                      <FaCalendarAlt className="text-muted me-2" size={12} />
                      <Input type="date" className="border-0 bg-transparent shadow-none form-control-sm" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                      <span className="mx-1 text-muted">to</span>
                      <Input type="date" className="border-0 bg-transparent shadow-none form-control-sm" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                  </div>
                  <Row>
                    <Col md={6}>
                      <p className="text-muted font-size-13 mb-3"><FaChartLine className="me-1 text-primary"/> New Customers</p>
                      <div style={{ height: "250px" }}>
                        <Line options={chartOptions} data={{ labels: customerStats.labels, datasets: [{ label: 'New', data: customerStats.values, borderColor: '#556ee6', backgroundColor: 'rgba(85, 110, 230, 0.1)', fill: true, tension: 0.4 }] }} />
                      </div>
                    </Col>
                    <Col md={6}>
                      <p className="text-muted font-size-13 mb-3"><FaCloudUploadAlt className="me-1 text-success"/> File Uploads</p>
                      <div style={{ height: "250px" }}>
                        <Bar options={chartOptions} data={{ labels: fileStats.labels, datasets: [{ label: 'Files', data: fileStats.values, backgroundColor: '#34c38f', borderRadius: 4 }] }} />
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* ROW 3: RECENT ACTIVITY TABLES */}
          <Row className="mb-4">
            <Col xl={6} className="mb-3 mb-xl-0">
              <Card className="border-0 shadow-sm">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">Recent Customers</h5>
                    <button className="btn btn-sm btn-link text-primary p-0" onClick={() => navigate(`${Configs.appurl}customers`)}>View All <FaArrowRight size={10}/></button>
                  </div>
                  <div className="table-responsive">
                    <Table className="table-hover mb-0">
                      <thead className="table-light text-muted"><tr><th>Name</th><th>Email</th><th>Phone</th><th className="text-center">Members</th></tr></thead>
                      <tbody>
                        {customersData.slice(0, 5).map(c => (
                          <tr key={c.id}>
                            <td className="fw-medium">{c.customerName}</td>
                            <td className="text-muted text-truncate" style={{maxWidth: '150px'}}>{c.email || 'N/A'}</td>
                            <td>{c.phone}</td>
                            <td className="text-center"><Badge color="soft-info" className="badge-soft-info">{c.members?.length || 0}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={6}>
              <Card className="border-0 shadow-sm">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">Staff Status</h5>
                    <button className="btn btn-sm btn-link text-primary p-0" onClick={() => navigate(`${Configs.appurl}staff`)}>View All <FaArrowRight size={10}/></button>
                  </div>
                  <div className="table-responsive">
                    <Table className="table-hover mb-0">
                      <thead className="table-light text-muted"><tr><th>Name</th><th>Email</th><th>Role</th><th className="text-center">Status</th></tr></thead>
                      <tbody>
                        {staffData.slice(0, 5).map(s => (
                          <tr key={s.id}>
                            <td className="fw-medium">{s.name}</td>
                            <td className="text-muted text-truncate" style={{maxWidth: '150px'}}>{s.email || 'N/A'}</td>
                            <td className="text-muted">{s.designation}</td>
                            <td className="text-center"><Badge pill className={`badge-soft-${s.status ? 'success' : 'danger'}`}>{s.status ? "Active" : "Inactive"}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* ROW 4: WELCOME + DOC CATEGORIES + DOC TYPES */}
          <Row>
            <Col lg={4} className="mb-3 mb-lg-0">
               <WelcomeComp />
            </Col>
            
            <Col lg={4} className="mb-3 mb-lg-0">
              <Card className="border-0 shadow-sm h-100">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">Document Categories</h5>
                    <button className="btn btn-sm btn-soft-primary" onClick={() => navigate(`${Configs.appurl}categories`)}>All</button>
                  </div>
                  <div className="table-responsive">
                    <Table className="table-sm mb-0">
                      <tbody>
                        {categoryTypesData.slice(0, 5).map((cat, idx) => (
                          <tr key={cat.id || idx}>
                            <td className="py-2 border-0"><FaFolderOpen className="text-warning me-2"/> {cat.documentCategory}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
            
            <Col lg={4}>
              <Card className="border-0 shadow-sm h-100">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold mb-0">Document Types</h5>
                    <button className="btn btn-sm btn-soft-primary" onClick={() => navigate(`${Configs.appurl}document-types`)}>All</button>
                  </div>
                  <div className="table-responsive">
                    <Table className="table-sm mb-0">
                      <tbody>
                        {docTypesData.slice(0, 5).map((d, idx) => (
                          <tr key={d.id || idx}>
                            <td className="py-2 border-0"><FaFileAlt className="text-primary me-2"/> {d.documentType}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default Dashboard